import { tns } from 'tiny-slider/src/tiny-slider'
import debounce from 'views/utils/debounce'
import toggleBoolStringAttr from 'views/utils/toggleBoolStringAttribute'
import { ARIA_HIDDEN } from 'views/assets/scripts/consts'
import {
  GALLERY_MAJOR_SELECTOR,
  NAV_SMALLSCREEN_SELECTOR,
  OVERLAY_SELECTOR,
  SLIDE_ANCHOR_SELECTED_CLASS,
  SLIDE_ANCHOR_SELECTOR,
  SLIDE_ITEM_SELECTOR,
  SLIDER_NAV_NEXT_SELECTOR,
  SLIDER_NAV_PREV_SELECTOR,
  SLIDER_PREVENT_CLICK_SELECTOR,
  THUMBNAIL_SLIDER_SELECTOR,
  SLIDE_ACTIVE_CLASS,
  SLIDER_ITEMS_SELECTOR
} from 'views/assets/scripts/MediaGallery/consts'
import { BREAKPOINT_MEDIUM } from 'views/consts'

import {
  bindThumbsClick,
  buildScopedSliderSelector,
  getImageToSlideMap,
  setSelectedFirstThumbnail,
  getSliderBaseOptions,
  updateHeight
} from 'views/assets/scripts/MediaGallery/helpers'

const isLargeViewport = window.matchMedia(BREAKPOINT_MEDIUM)

export default class Slider {
  constructor(ctx) {
    this.scope = ctx.galleryElement.dataset.scope
    this.scopedSliderSelector = buildScopedSliderSelector(this.scope)
    this.thumbnailHasChanged = false
    this.debounceTime = 200
    this.galleryOverlay = ctx.galleryElement.querySelector(OVERLAY_SELECTOR)

    this.init(ctx)
  }

  // TODO: split up further; eg. move majorSlider initalisation
  // and register/attach event handlers into separate fns
  init = ctx => {
    const { galleryElement } = ctx

    galleryElement.classList.add('js-tiny-slider')
    const list = document.querySelector(
      this.scopedSliderSelector(GALLERY_MAJOR_SELECTOR)
    )

    if (!ctx.majorSlider) {
      ctx.majorSlider = tns({
        ...getSliderBaseOptions(),
        mode: ctx.isDesktop ? 'gallery' : 'carousel',
        navAsThumbnails: false,
        container: this.scopedSliderSelector(GALLERY_MAJOR_SELECTOR),
        navContainer: this.scopedSliderSelector(NAV_SMALLSCREEN_SELECTOR)
      })
      const imageToSlideMap = getImageToSlideMap(
        ctx.galleryElement.querySelectorAll(SLIDE_ITEM_SELECTOR)
      )

      const onRequestAnimationFrame = () => {
        const navThumbsList = [
          ...ctx.galleryElement.querySelectorAll(SLIDER_PREVENT_CLICK_SELECTOR)
        ]

        bindThumbsClick({
          navThumbsList,
          majorSlider: ctx.majorSlider,
          imageToSlideMap
        })
        setSelectedFirstThumbnail(navThumbsList)
      }

      // On the next animation frame when all the slides processed by tns init
      requestAnimationFrame(() => onRequestAnimationFrame())

      updateHeight(list)
    }

    this.initThumbnailSlider(ctx)

    window.addEventListener(
      'resize',
      debounce(() => {
        if (ctx.isLargeViewport !== isLargeViewport.matches) {
          ctx.isLargeViewport = !ctx.isLargeViewport
        }
        updateHeight(list)
      }, this.debounceTime)
    )

    this.registerOnChangeEvent(ctx)
  }

  initThumbnailSlider = ctx => {
    const thumbnailSliderContainer = ctx.galleryElement.querySelector(
      THUMBNAIL_SLIDER_SELECTOR
    )

    if (!ctx.thumbnailSlider && thumbnailSliderContainer) {
      const sliderItems = thumbnailSliderContainer.querySelector(
        SLIDER_ITEMS_SELECTOR
      )
      const items =
        parseInt(
          thumbnailSliderContainer.getAttribute('data-slider-slideby'),
          10
        ) || 1
      const buttonPrev = thumbnailSliderContainer.querySelector(
        SLIDER_NAV_PREV_SELECTOR
      )
      const buttonNext = thumbnailSliderContainer.querySelector(
        SLIDER_NAV_NEXT_SELECTOR
      )

      ctx.thumbnailSlider = tns({
        autoplay: false,
        nav: false,
        gutter: 10,
        loop: false,
        container: sliderItems,
        slideBy: 1,
        items
      })

      if (buttonPrev && buttonNext) {
        const itemsCount = sliderItems.children.length

        if (itemsCount <= items) {
          toggleBoolStringAttr(buttonPrev, ARIA_HIDDEN, true)
          toggleBoolStringAttr(buttonNext, ARIA_HIDDEN, true)
        } else {
          buttonPrev.disabled = true
        }

        const getCurrentIndex = () => {
          return ctx.majorSlider.getInfo().index
        }

        buttonPrev.addEventListener('click', () => {
          const currentIndex = getCurrentIndex()

          if (currentIndex > 0) {
            const prev = currentIndex - 1
            buttonNext.disabled = false
            // { ctx, currentIndex: prev, back: true }
            this.handleThumbnailSliderIndexChange({
              ctx,
              currentIndex: prev,
              back: true
            })

            if (prev === 0) {
              buttonPrev.disabled = true
            }
          }
        })

        buttonNext.addEventListener('click', () => {
          const currentIndex = getCurrentIndex()

          if (currentIndex < itemsCount - 1) {
            const next = currentIndex + 1
            buttonPrev.disabled = false
            // { ctx, currentIndex, back }
            this.handleThumbnailSliderIndexChange({
              ctx,
              currentIndex: next,
              back: false
            })

            if (next === itemsCount - 1) {
              buttonNext.disabled = true
            }
          }
        })
      }

      // Prevent opening image src
      Array.prototype.forEach.call(
        sliderItems.querySelectorAll(SLIDER_PREVENT_CLICK_SELECTOR),
        link => {
          link.addEventListener('click', e => {
            e.preventDefault()
          })
        }
      )
    }
  }

  handleThumbnailSliderIndexChange = ({ ctx, currentIndex, back }) => {
    ctx.majorSlider.goTo(currentIndex)

    const info = ctx.thumbnailSlider.getInfo()

    Array.prototype.forEach.call([...info.slideItems], slide => {
      slide
        .querySelector(SLIDE_ANCHOR_SELECTOR)
        .classList.remove(SLIDE_ANCHOR_SELECTED_CLASS)
    })

    const currentSlide = info.slideItems[currentIndex]
    currentSlide
      .querySelector(SLIDE_ANCHOR_SELECTOR)
      .classList.add(SLIDE_ANCHOR_SELECTED_CLASS)

    if (!currentSlide.classList.contains(SLIDE_ACTIVE_CLASS)) {
      const index = back ? currentIndex : currentIndex + 1 - info.items
      ctx.thumbnailSlider.goTo(index)
    }
  }

  registerOnChangeEvent = ctx => {
    const onChangeEvents = () => {
      ctx.handleRedPointDisplay(ctx.majorSlider, this.galleryOverlay)
    }

    if (ctx.majorSlider?.events) {
      ctx.majorSlider.events.on('indexChanged', onChangeEvents)
    }
  }
}
